// Breach Step 9: Review & Submit
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Checkbox } from '../../../../components/ui/checkbox';
import { CalendarInput } from '../../../../components/ui/calendar-input';
import { getRiskBand, SUPERVISORY_AUTHORITIES, MAX_POINTS } from '../constants';
import { PointsBar } from '../../../../components/workflow';

export default function Step9Review({ formData, updateField, goToStep, handleSubmit, saving, pointsBreakdown, volScore, contScore, user }) {
  const isDpoRole = ['dpo', 'super_admin', 'org_owner'].includes(user?.role);
  const overSev = formData.override_severity;
  const overLik = formData.override_likelihood;
  const autoScore = formData.auto_severity && formData.auto_likelihood ? formData.auto_severity * formData.auto_likelihood : null;
  const overScore = overSev && overLik ? overSev * overLik : null;
  const finalScore = overScore || autoScore;
  const finalBand = finalScore ? getRiskBand(finalScore) : null;

  const sections = [
    { step: 1, title: 'Initial Report', items: [
      { l: 'Classification', v: formData.incident_or_breach },
      { l: 'Title', v: formData.title },
      { l: 'Awareness Date', v: formData.awareness_date },
      { l: 'Breach Date', v: formData.breach_date || 'Not specified' },
      { l: 'Description', v: formData.what_happened },
    ]},
    { step: 2, title: 'Classification', items: [
      { l: 'Breach Type', v: formData.breach_types.join(', ') || 'None' },
      { l: 'Origin', v: formData.origin || 'Not specified' },
      { l: 'Systems', v: formData.systems_affected.join(', ') || 'None' },
    ]},
    { step: 3, title: 'Root Cause', items: [{ l: 'Causes', v: formData.root_causes.join(', ') || 'None' }] },
    { step: 4, title: 'Data Affected', items: [
      { l: 'Data Categories', v: formData.data_categories.length ? `${formData.data_categories.length} selected (${pointsBreakdown.data_sensitivity} sensitivity pts, ${pointsBreakdown.identification} ID pts)` : 'None' },
      { l: 'Who Affected', v: formData.who_affected.join(', ') || 'None' },
      { l: 'Number Affected', v: `${formData.number_affected}${formData.number_affected_estimate ? ' (estimate)' : ''} — Volume: ${volScore}/100` },
    ]},
    { step: 5, title: 'Response', items: [
      { l: 'Actions Taken', v: formData.actions_taken || 'Not documented' },
      { l: 'Containment', v: `${formData.containment_measures.length}/11 measures — Score: ${contScore}` },
      { l: 'Incident Owner', v: formData.incident_owner || 'Not assigned' },
    ]},
    { step: 7, title: 'Notifications', items: [
      { l: 'Authority', v: SUPERVISORY_AUTHORITIES.find(a => a.value === formData.supervisory_authority)?.label || 'Not selected' },
      { l: 'Date Notified', v: formData.date_notified || 'Not yet' },
      { l: 'Subject Notification', v: formData.subject_notification || 'Not determined' },
    ]},
    { step: 8, title: 'Remediation', items: [
      { l: 'Remediation Actions', v: (formData.remediation_actions?.length || 0) ? `${formData.remediation_actions.length} action(s) documented` : 'None' },
    ]},
  ];

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Review & Submit</h2><p className="text-muted-foreground mt-1">Review all information before submitting.</p></div>

      {finalBand && (
        <Card className="border-2" style={{ borderColor: finalBand.color }}>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Risk Matrix Result</p>
                <div className="flex items-center gap-4">
                  <div className="text-5xl font-bold font-mono" style={{ color: finalBand.color }}>{finalScore}</div>
                  <div>
                    <Badge style={{ backgroundColor: finalBand.color, color: '#fff' }}>{finalBand.level}</Badge>
                    {overScore && <p className="text-xs text-muted-foreground mt-1">DPO Override (auto: {autoScore})</p>}
                  </div>
                </div>
                <p className="text-sm mt-2">{finalBand.action}</p>
                <p className="text-xs text-muted-foreground mt-1">{finalBand.ref}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Points Breakdown</p>
                <PointsBar label="Data Sensitivity" current={pointsBreakdown.data_sensitivity} max={MAX_POINTS.data_sensitivity} />
                <PointsBar label="Identification" current={pointsBreakdown.identification} max={MAX_POINTS.identification} />
                <PointsBar label="Vulnerability" current={pointsBreakdown.vulnerability} max={MAX_POINTS.vulnerability} />
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Volume</span><span className="font-mono">{volScore}/100</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Containment</span><span className="font-mono">{contScore}/100</span></div>
              </div>
            </div>
            {formData.override_justification && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground">DPO Override Justification:</p>
                <p className="text-sm mt-1">{formData.override_justification}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {formData.impact_areas.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><div className="flex items-center justify-between">
            <CardTitle className="text-base">Step 6: Impact Areas & Risk</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => goToStep(6)} className="text-primary text-xs">Edit</Button>
          </div></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {formData.impact_areas.map(a => <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>)}
            </div>
            {formData.risk_score != null && (
              <p className="text-xs text-muted-foreground mt-2">Composite fuzzy score: {formData.risk_score}/100 ({formData.risk_label})</p>
            )}
          </CardContent>
        </Card>
      )}

      {sections.map(section => (
        <Card key={section.step}>
          <CardHeader className="pb-2"><div className="flex items-center justify-between">
            <CardTitle className="text-base">Step {section.step}: {section.title}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => goToStep(section.step)} className="text-primary text-xs">Edit</Button>
          </div></CardHeader>
          <CardContent><div className="space-y-2">
            {section.items.map((item, i) => (
              <div key={i} className="flex gap-2"><span className="text-sm text-muted-foreground w-36 shrink-0">{item.l}:</span><span className="text-sm">{item.v}</span></div>
            ))}
          </div></CardContent>
        </Card>
      ))}

      {isDpoRole && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">DPO Sign-Off</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>DPO Name <span className="text-destructive">*</span></Label>
              <Input
                value={formData.dpo_signoff_name || ''}
                onChange={(e) => updateField('dpo_signoff_name', e.target.value)}
                placeholder="Enter your name"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Sign-Off Date <span className="text-destructive">*</span></Label>
              <CalendarInput
                value={formData.dpo_signoff_date || ''}
                onChange={(date) => updateField('dpo_signoff_date', date)}
                defaultToday={true}
                placeholder="DD/MM/YYYY"
                className="mt-1.5"
              />
            </div>
            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                checked={formData.dpo_signoff_confirmed || false}
                onCheckedChange={(checked) => updateField('dpo_signoff_confirmed', checked)}
              />
              <label className="text-sm cursor-pointer flex-1">
                I confirm this breach report is accurate and complete, and all required information has been reviewed and assessed.
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-center pt-4">
        <Button
          data-testid="submit-report"
          size="lg"
          onClick={handleSubmit}
          disabled={saving || (isDpoRole && !formData.dpo_signoff_confirmed)}
          className="px-8"
        >
          {saving ? 'Submitting...' : 'Submit Breach Report'}
        </Button>
      </div>
    </div>
  );
}
